import type { CardFlavour } from '@components/Card2/types';
import type { ProfileOption } from '@components/Profile/UtilsComponents/ProfilesSelect';
import type { ProfileEssentialDetailsFragment } from '@graphql/fragments/profile/core.graphql';
import { logger } from '@modules/logger';
import { createContext } from 'react';

import type { CardAttributeType, CardField } from '.cache/__types__';

const initialContext: CardEditContextValues = {
  cardId: undefined,
  activeAttributeId: undefined,
  activeAttribute: undefined,
  editPageMode: undefined,
  pendingCardField: undefined,
  flavour: undefined,
  canAssignMultipleProfiles: false,
  selectedProfiles: [],
  onSelectNewProfiles: () => logger.warn('onSelectNewProfiles not provided for cardFieldEditContext'),
  selectedProfilesContainInvalidEmails: false,
  selectedProfilesExceedMaximum: false,
  selectedProfilesWithInvalidDomains: [],
  isCardOwner: false,
  loading: undefined,
  cardProfile: undefined,
  setCardId: () => logger.warn('setCardId not provided for cardFieldEditContext'),
  showRequiredAttributesErrors: false,
  setPendingCardField: () => logger.warn('setPedningCardField not provided for cardFieldEditContext'),
  requestToMakeActive: () => logger.warn('requestToMakeActive not provided for cardFieldEditContext'),
  makeInactive: () => logger.warn('makeInactive not provided for cardFieldEditContext'),
  setActiveAttribute: () => logger.warn('setActiveAttribute not provided for cardFieldEditContext'),
  setShowRequiredAttributesErrors: () =>
    logger.warn('setShowRequiredAttributesErrors not provided for cardEditContext'),
};

interface CardEditContextValues {
  cardId: string | undefined;
  activeAttributeId: string | undefined;
  activeAttribute: CardAttributeType | undefined;
  /**
   * Dictates if we are creating a card or updating a card in the edit page
   */
  editPageMode?: 'create' | 'update';
  flavour: CardFlavour | undefined;
  canAssignMultipleProfiles?: boolean;
  selectedProfiles?: readonly ProfileOption[];
  onSelectNewProfiles?: (profiles: readonly ProfileOption[]) => void;
  selectedProfilesContainInvalidEmails?: boolean;
  selectedProfilesExceedMaximum?: boolean;
  selectedProfilesWithInvalidDomains?: string[];
  isCardOwner: boolean | undefined;
  cardProfile?: ProfileEssentialDetailsFragment;
  requestToMakeActive: (attributeId: string, editPageMode: 'create' | 'update') => void;
  makeInactive: (attributeId: string) => void;
  pendingCardField: CardField | undefined;
  setPendingCardField: (field: CardField | undefined) => void;
  setActiveAttribute: (attribute: CardAttributeType | undefined) => void;
  setCardId: (id: string | undefined) => void;
  /** When the card edit experience is in a loading state, this could be due to a network request or other reasons.
   * As a child of this context, it's your job to decide how to handle this loading state.
   */
  loading: boolean | undefined;
  showRequiredAttributesErrors?: boolean | undefined;
  setShowRequiredAttributesErrors?: React.Dispatch<React.SetStateAction<boolean>>;
  showSsoDomainError?: boolean | undefined;
  setShowSsoDomainError?: React.Dispatch<React.SetStateAction<boolean>>;
  onEditEvent?: (event: 'open' | 'cancel' | 'save' | 'delete', attributeName: string) => void;
}

const cardEditContext = createContext<CardEditContextValues>(initialContext);

export { cardEditContext };
